import carens from "../../../constants/Invoice/carens_details";
import jinkies from "../../../constants/Invoice/jinkies_details";
import quilaD from "../../../constants/Invoice/quilaD_details";
import ranch from "../../../constants/Invoice/ranch_details";
import wagg from "../../../constants/Invoice/wagg_details";
import seltous from "../../../constants/Invoice/seltous_details";
import vexora from "../../../constants/Invoice/vexora_details";
import wavepay from "../../../constants/Invoice/wavepay_details";

const orgMap = {
  carens,
  jinkies,
  quilaD,
  ranch,
  wagg,
  vexora,
  wavepay,
  seltous,
};

const fetchProducts = async (orgName, amount) => {
  const orgData = orgMap[orgName];
  if (!orgData) {
    throw new Error(`Unknown organization: ${orgName}`);
  }

  const products = orgData.products;
  const data = getMinProductCombination(products, amount);

  return data;
};

function getMinProductCombination(products, totalAmount) {
  const productEntries = Object.entries(products).map(([name, amount]) => ({
    name,
    amount,
  }));

  const baseAmount = parseFloat((totalAmount / 1.18).toFixed(2));
  const gstValue = parseFloat((totalAmount - baseAmount).toFixed(2));

  let bestCombination = null;
  let bestDiscount = null;

  // Sort descending for greedy
  const sortedProducts = [...productEntries].sort(
    (a, b) => b.amount - a.amount
  );

  // ✅ Step 1: Try greedy first
  const greedyResult = findGreedyCombination(sortedProducts, baseAmount);
  if (greedyResult) {
    const productTotal = greedyResult.reduce((sum, p) => sum + p.amount, 0);
    const discount = parseFloat((productTotal - baseAmount).toFixed(2));

    if (discount >= 0 && discount <= 500) {
      return {
        products: greedyResult,
        discount,
        productTotal: parseFloat(productTotal.toFixed(2)),
        gstValue,
        taxableAmount: baseAmount,
      };
    }
  }

  // ✅ Step 2: Fallback to DP with discount scan range
  for (
    let target = Math.ceil(baseAmount);
    target <= Math.floor(baseAmount + 500);
    target++
  ) {
    const exactMatch = productEntries.find((p) => p.amount === target);
    const exactDiscount = parseFloat((target - baseAmount).toFixed(2));

    if (exactMatch && exactDiscount <= 500) {
      bestCombination = [exactMatch];
      bestDiscount = exactDiscount;
      break;
    }

    const result = findMinCombinationDP(sortedProducts, target);

    if (result) {
      const productTotal = result.reduce((sum, p) => sum + p.amount, 0);
      const discount = parseFloat((productTotal - baseAmount).toFixed(2));

      if (discount >= 0 && discount <= 500) {
        bestCombination = result;
        bestDiscount = discount;
        break;
      }
    }
  }

  const productTotal = bestCombination
    ? parseFloat(
        bestCombination.reduce((sum, p) => sum + p.amount, 0).toFixed(2)
      )
    : 0;

  return {
    products: bestCombination || [],
    discount: bestCombination ? bestDiscount : null,
    productTotal,
    gstValue,
    taxableAmount: baseAmount,
  };
}

// ✅ Efficient DP for exact match with least number of products
function findMinCombinationDP(products, target) {
  const dp = new Map();
  dp.set(0, []);

  for (const { name, amount } of products) {
    const currentDp = new Map(dp);

    for (const [sum, combo] of currentDp.entries()) {
      const newSum = sum + amount;
      if (newSum > target) continue;

      const newCombo = [...combo, { name, amount }];
      if (!dp.has(newSum) || newCombo.length < dp.get(newSum).length) {
        dp.set(newSum, newCombo);
      }
    }
  }

  return dp.get(target) || null;
}

// ✅ Greedy logic: pick largest amounts first until sum >= baseAmount
function findGreedyCombination(products, baseAmount) {
  const result = [];
  let sum = 0;

  for (const { name, amount } of products) {
    if (sum + amount <= baseAmount + 500) {
      result.push({ name, amount });
      sum += amount;
    }

    if (sum >= baseAmount) {
      return result;
    }
  }

  return null;
}

export default fetchProducts;
